import { Skeleton } from "@chakra-ui/react"
import SubmitButton from "@pathwright/ui/src/components/button/SubmitButton"
import Card from "@pathwright/ui/src/components/card/Card"
import CardBlock from "@pathwright/ui/src/components/card/CardBlock"
import TextInput from "@pathwright/ui/src/components/form/form-text-input/TextInput"
import useDebouncedState from "@pathwright/ui/src/components/hooks/useDebouncedState"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import get from "lodash/get"
import React from "react"
// @ts-ignore
import { Field, Form, Submit } from "react-connect-form-forked"
import {
  MentorGroupType,
  MentorGroupsDocument,
  useCreateMentorGroupMutation,
  useMentorGroupsQuery
} from "../../../api/generated"
import SelectInput from "../../../lib/SelectInput/SelectInput"
import { flattenEdges, usePaginator } from "../../../utils/apollo"
import UpgradeMentorGroupsGate from "./UpgradeMentorGroupsGate"

interface MentorGroupFormProps {
  parentGroupId?: number | null
  onSave: () => void
}

export let MentorGroupForm: React.FC<MentorGroupFormProps> = ({
  parentGroupId,
  onSave
}) => {
  const { t } = useTranslate()
  const [search, setSearch] = useDebouncedState("")
  const [createMentorGroup] = useCreateMentorGroupMutation()

  const mentorGroupsQuery = useMentorGroupsQuery({
    variables: {
      seatLimitFilter: { neq: 1 },
      search: search || undefined
    }
  })

  const groups = mentorGroupsQuery.data
    ? flattenEdges(get(mentorGroupsQuery, "data.school.mentor_groups"))
    : []

  // Get total count from GraphQL response
  const totalGroups = get(
    mentorGroupsQuery,
    "data.school.mentor_groups.total",
    0
  )

  const { loadMore, hasMore, loadingMore } = usePaginator({
    data: mentorGroupsQuery, // hacky
    path: "school.mentor_groups"
  })

  // Show the parent field if:
  // 1. There are groups available (total > 0), OR
  // 2. User is actively searching (search is not null/empty)
  const shouldShowParentField =
    totalGroups > 0 || (search && search.trim() !== "")

  const handleSubmit = async ({
    name,
    parent,
    type
  }: {
    name: string
    parent?: string | null
    type?: MentorGroupType
  }) => {
    const selectedParentId = parent ? parseInt(parent) : null

    await createMentorGroup({
      variables: {
        name,
        parent: selectedParentId,
        type
      },
      refetchQueries: [
        {
          query: MentorGroupsDocument,
          variables: {
            parentId: { eq: selectedParentId },
            seatLimitFilter: { neq: 1 }
          }
        }
      ]
    })
  }

  return (
    <UpgradeMentorGroupsGate>
      <Form
        initialValue={{
          parent: parentGroupId || null
        }}
        onSubmit={handleSubmit}
        onSubmitSuccess={onSave}
      >
        <CardBlock>
          <Field
            name="name"
            type="text"
            label={t("mentor_group.form.fields.name.label")}
            placeholder={t("mentor_group.form.fields.name.placeholder")}
            topLabel
            hideStatus
            component={TextInput}
            style={{ flexGrow: 1 }}
            required
          />
          {mentorGroupsQuery.loading && !shouldShowParentField ? (
            <div>
              <Skeleton height="20px" mb={2} />
              <Skeleton height="40px" />
            </div>
          ) : shouldShowParentField ? (
            <Field
              name="parent"
              type="select"
              label={t("mentor_group.form.fields.parent.label")}
              instructions={t("mentor_group.form.fields.parent.instructions")}
              topLabel
              hideStatus
              simpleValue
              searchable
              component={SelectInput}
              placeholder={t("mentor_group.form.fields.select")}
              options={groups}
              labelKey="name"
              valueKey="id"
              onMenuScrollToBottom={() => !loadingMore && hasMore && loadMore()}
              onInputChange={(val: string) => {
                setSearch(val)
                // return val for react-select to handle
                return val
              }}
            />
          ) : null}
        </CardBlock>
        <Submit styleType="primary" component={SubmitButton}>
          {t("mentor_group.form.create")}
        </Submit>
      </Form>
    </UpgradeMentorGroupsGate>
  )
}

interface AddMentorGroupCardProps {
  card: {
    onClose: () => void
  }
  parentGroupId?: number | null
}

const AddMentorGroupCard: React.FC<AddMentorGroupCardProps> = ({
  card,
  parentGroupId
}) => {
  const { t } = useTranslate()
  const title = parentGroupId
    ? t("mentor_group.new_sub_group")
    : t("mentor_group.new_group")

  return (
    <Card card={card} title={title}>
      <MentorGroupForm parentGroupId={parentGroupId} onSave={card.onClose} />
    </Card>
  )
}

export default AddMentorGroupCard
